import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Recycle, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

// Stage-waste Trend Report — surface which stages leak the most yield across jobs
export default function StageWasteTrendReport() {
  // A dashboard tile ("Good output" / "Wastage") lands here with its own window in ?from=&to=,
  // so the report opens on the same period the card's % was computed from, not all-time.
  const [params] = useSearchParams();
  const [from, setFrom] = useState(params.get("from") || "");
  const [to, setTo] = useState(params.get("to") || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = (f = from, t = to) => {
    setLoading(true);
    const p = {};
    if (f) p.date_from = f;
    if (t) p.date_to = t;
    api.get("/reports/stage-waste-trend", { params: p })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => load(), []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="p-6 text-sm text-neutral-500">Loading waste trend…</div>;
  if (!data) return null;

  const maxPct = Math.max(6, ...data.stages.map((s) => Math.max(s.avg_waste_pct, s.max_waste_pct)));

  return (
    <div className="space-y-6" data-testid="stage-waste-trend">
      <div className="flex flex-wrap items-end gap-2 text-xs">
        <label className="flex flex-col gap-1">
          <span className="text-neutral-500">From</span>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-8 text-xs" data-testid="waste-trend-from" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-neutral-500">To</span>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-8 text-xs" data-testid="waste-trend-to" />
        </label>
        <Button size="sm" variant="outline" className="h-8" onClick={() => load()} data-testid="waste-trend-apply">Apply</Button>
        {(from || to) && (
          <Button size="sm" variant="ghost" className="h-8" onClick={() => { setFrom(""); setTo(""); load("", ""); }}>
            Clear (all-time)
          </Button>
        )}
      </div>

      <div className="text-xs text-neutral-600 bg-neutral-50 border border-neutral-200 rounded-sm p-3">
        <span className="font-semibold">Total jobs with production data{from || to ? " in this period" : ""}:</span>{" "}
        <span className="font-mono-num">{data.total_jobs_with_stages}</span>
        <span className="mx-3">·</span>
        <span className="text-neutral-500">Bars show <em>average</em> and <em>max</em> waste % per stage. Rows below list every job over the alert threshold.</span>
      </div>

      {/* Bar chart section */}
      <div className="border border-neutral-200 rounded-sm bg-white p-4">
        <h3 className="text-sm font-semibold text-neutral-800 mb-3 flex items-center gap-2">
          <Recycle size={14} className="text-[#FF5A00]" />
          Stage Waste — Average vs Max
        </h3>
        <div className="space-y-3">
          {data.stages.map((s) => (
            <StageBar key={s.stage} s={s} maxPct={maxPct} />
          ))}
        </div>
      </div>

      {/* Hot job rows per stage */}
      {data.stages.map((s) => (
        s.hot_jobs.length > 0 && (
          <div key={`hot-${s.stage}`} className="border border-red-200 rounded-sm bg-white overflow-hidden">
            <div className="bg-red-50 border-b border-red-200 px-4 py-2 flex items-center justify-between">
              <div className="text-sm font-semibold text-red-800">
                {s.label} — jobs over {s.alert_threshold}% alert threshold
              </div>
              <div className="text-[10px] font-semibold text-red-700 bg-red-100 border border-red-200 rounded-sm px-2 py-0.5">
                {s.hot_jobs.length} problem job{s.hot_jobs.length === 1 ? "" : "s"}
              </div>
            </div>
            <table className="w-full text-xs" data-testid={`waste-hot-${s.stage}`}>
              <thead className="bg-neutral-50 text-neutral-600 uppercase text-[10px]">
                <tr>
                  <th className="text-left px-3 py-2">MPWO</th>
                  <th className="text-left px-3 py-2">Customer</th>
                  <th className="text-left px-3 py-2">Product</th>
                  <th className="text-right px-3 py-2">Input</th>
                  <th className="text-right px-3 py-2">Waste</th>
                  <th className="text-right px-3 py-2">Waste %</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {s.hot_jobs.map((j) => (
                  <tr key={j.mpwo} className="border-t border-neutral-100 hover:bg-neutral-50">
                    <td className="px-3 py-2 font-mono-num">{j.mpwo}</td>
                    <td className="px-3 py-2">{j.customer || "—"}</td>
                    <td className="px-3 py-2 text-neutral-600">{j.product || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono-num">{j.input_qty.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right font-mono-num text-red-700">{j.waste_qty.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right font-mono-num font-bold text-red-700">{j.waste_pct.toFixed(1)}%</td>
                    <td className="px-3 py-2 text-right">
                      <Link
                        to={`/orders/${j.id || j.mpwo}`}
                        className="text-[#FF5A00] hover:underline inline-flex items-center gap-1"
                      >
                        Open <ArrowRight size={11} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ))}
    </div>
  );
}

function StageBar({ s, maxPct }) {
  const avgW = (s.avg_waste_pct / maxPct) * 100;
  const maxW = (s.max_waste_pct / maxPct) * 100;
  const tone = (pct) => pct >= s.alert_threshold ? "bg-red-500"
                   : pct >= s.warn_threshold  ? "bg-amber-500"
                   : "bg-emerald-500";
  return (
    <div data-testid={`waste-bar-${s.stage}`}>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="font-semibold text-neutral-800">{s.label}</span>
        <span className="text-neutral-500">
          {s.jobs} job{s.jobs === 1 ? "" : "s"} · warn ≥ {s.warn_threshold}% · alert ≥ {s.alert_threshold}%
        </span>
      </div>
      <div className="relative h-6 bg-neutral-100 rounded-sm overflow-hidden">
        {/* Average waste bar */}
        <div
          className={`absolute inset-y-0 left-0 ${tone(s.avg_waste_pct)} opacity-90 transition-all`}
          style={{ width: `${avgW}%` }}
          title={`Average ${s.avg_waste_pct.toFixed(2)}%`}
        />
        {/* Max marker */}
        <div
          className="absolute inset-y-0 border-l-2 border-red-800"
          style={{ left: `${maxW}%` }}
          title={`Max ${s.max_waste_pct.toFixed(2)}% — ${s.max_job || "—"}`}
        />
      </div>
      <div className="flex items-center gap-4 text-[10px] text-neutral-500 mt-1 font-mono-num">
        <span>Avg <b className="text-neutral-800">{s.avg_waste_pct.toFixed(2)}%</b></span>
        <span>Max <b className="text-red-700">{s.max_waste_pct.toFixed(2)}%</b>{s.max_job ? ` · ${s.max_job}` : ""}</span>
      </div>
    </div>
  );
}
